import {
    statusColorForReleaseStatus,
    isPhasedReleaseComplete,
    isReleaseSkipped,
    isPhasedReleasePausedByRunway,
    isReleaseComplete,
} from '@/helpers';
import { displayStringForRolloutPercentage } from '@/helpers/displayHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectOrg,
    selectApp,
    selectRelease,
    selectAppStoreType,
    selectHasVCSIntegrationConnected,
    selectIsWorkflowsRolloutStepEnabled,
} from '@/selectors/selectors';
import {
    ReleaseTimelinePhase,
    ReleaseStatus,
    ReleaseCreatedOrigin,
    ReleaseType,
    ReleaseNav,
    ReleaseStepType,
} from '@/types';
import { ReleaseComponentProps, StringKeyedDictionary } from '@/typescriptHelpers';
import { WarningFilled, FireOutlined, UndoOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as AnalyticEvents from '../../utils/analytics';
import * as React from 'react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import StatusNode from '../StatusNode';
import styles from '../styles/timelineStyles';
import { runwayColors, antGrey, releaseMetaTextStyle } from '../styles/globalStyles';
import * as Storage from '../../helpers/localStorageHelper';
import {
    createReleaseOverviewRoute,
    createReleaseRolloutRoute,
    createFeatureFlagsRoute,
    createReleaseStepTabRoute,
    ReleaseStepTabType,
    ROUTER_REDIRECT,
    createReleaseCalendarRoute,
} from '@/helpers/routeHelpers';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Text from 'antd/lib/typography/Text';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import { useSidebarColors } from '../styles/themeMode';

interface ReleasesSidebarNodeProps extends ReleaseComponentProps {
    index: number;
    releasesCount: number;
    showConnectors: boolean;
}

const ReleasesSidebarNode: React.FC<ReleasesSidebarNodeProps> = (props) => {
    const { index, releasesCount } = props;
    const [isHovered, setIsHovered] = React.useState(false);
    const history = useHistory();
    const dispatch = useDispatch();

    const lastSelectedReleaseStepForRelease: StringKeyedDictionary<string> = Storage.getItem(
        Storage.LAST_VIEWED_RELEASE_STEP_FOR_RELEASE,
    );

    const orgId = useSelector((state: RootState) => selectOrg(state, props.orgId).orgId);
    const appId = useSelector((state: RootState) => selectApp(state, props.appId).appId);
    const {
        releaseStatus,
        releaseTimelinePhase,
        releaseType,
        releaseCreatedOrigin,
        releaseReleasedAt,
        releaseSkippedAt,
        releaseIsReleaseTagged,
        releaseId,
        releaseReleaseId,
        releaseDisplayableVersion,
        releaseSourceVersion,
        phasedReleaseHasBeenSupersededByNewRelease,
    } = useSelector((state: RootState) => {
        const release = selectRelease(state, props.releaseId);

        return {
            releaseStatus: release.status,
            releaseTimelinePhase: release.timelinePhase,
            releaseType: release.type,
            releaseCreatedOrigin: release.createdOrigin,
            releaseReleasedAt: release.releasedAt,
            releaseSkippedAt: release.skippedAt,
            releaseIsReleaseTagged: release.isReleaseTagged,
            releaseId: release.id,
            releaseReleaseId: release.releaseId,
            releaseDisplayableVersion: release.displayableVersion,
            releaseSourceVersion: release.sourceVersion,
            phasedReleaseHasBeenSupersededByNewRelease: release.phasedReleaseHasBeenSupersededByNewRelease,
        };
    }, shallowEqual);
    const phasedRelease = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).phasedRelease,
        shallowEqual,
    );
    const rollbackTargetVersionInfo = useSelector(
        (state: RootState) => selectRelease(state, props.releaseId).rollbackTargetVersionInfo,
        shallowEqual,
    );

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const hasVCSIntegrationConnected = useSelector((state: RootState) =>
        selectHasVCSIntegrationConnected(state, props.appId),
    );

    const isFirstNode = index === 0;
    const isLastNode = index === releasesCount - 1;
    const statusColor = statusColorForReleaseStatus(releaseStatus);
    const releaseIsCurrentOrRollback = [ReleaseTimelinePhase.Current, ReleaseTimelinePhase.Rollback].includes(
        releaseTimelinePhase,
    );
    const releaseCompleted = releaseStatus === ReleaseStatus.Completed;
    const isReleaseFromScraper = releaseCreatedOrigin === ReleaseCreatedOrigin.AppStoreScraper;

    const isPhased = !!phasedRelease;
    const phasedReleaseComplete = isPhasedReleaseComplete(phasedRelease);

    let phasedReleaseActiveOrSuperseded =
        isPhased &&
        releaseCompleted &&
        phasedRelease.phasedReleasePercent !== null &&
        phasedRelease.phasedReleasePercent !== 100;

    if (phasedReleaseHasBeenSupersededByNewRelease) {
        phasedReleaseActiveOrSuperseded = true;
    }

    const phasedReleaseSuperseded = phasedReleaseActiveOrSuperseded && !isFirstNode;

    const showPhasedHalfFilled = phasedReleaseActiveOrSuperseded && !phasedRelease?.isManuallyAcceleratedPhasedRollout;

    const isRolloutStepMigrated = useSelector((state: RootState) =>
        selectIsWorkflowsRolloutStepEnabled(state, props.appId),
    );

    const showPhasedText =
        showPhasedHalfFilled && !phasedReleaseComplete && !phasedReleaseSuperseded && !isRolloutStepMigrated;

    const showSkippedDate = isReleaseSkipped(releaseStatus) && releaseSkippedAt;
    const showReleaseDate = !showPhasedText && !showSkippedDate && releaseReleasedAt;

    const needsTag =
        releaseCompleted && releaseIsReleaseTagged === false && hasVCSIntegrationConnected && !isReleaseFromScraper;

    const colorOverride =
        showPhasedText && isPhasedReleasePausedByRunway(appStoreType, phasedRelease) ? runwayColors.statusRed : null;

    const releasePath = releaseLinkPath(
        releaseId,
        orgId,
        appId,
        releaseReleaseId,
        releaseStatus,
        releaseType,
        lastSelectedReleaseStepForRelease,
    );

    const node = (
        <StatusNode
            statusColor={colorOverride ?? statusColor}
            showHead={!isFirstNode && props.showConnectors}
            showTail={!isLastNode && props.showConnectors}
            halfFilled={showPhasedHalfFilled}
            outerBorderColor="transparent"
            outerBorderWidth={0}
            size={12}
        />
    );
    const sc = useSidebarColors();
    const isSkipped = isReleaseSkipped(releaseStatus);
    const versionTextColor = isSkipped ? sc.textMuted : sc.text;
    const normalReleaseStyle: React.CSSProperties = {
        fontSize: 14,
        color: versionTextColor,
        textDecoration: isReleaseSkipped(releaseStatus) ? 'line-through' : 'none',
    };
    const normalContainerStyle: React.CSSProperties = {
        ...styles.timelineContent,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        gap: 20,
        borderTop: `0.5px solid ${sc.border}`,
        borderBottom: index === releasesCount - 1 ? `0.5px solid ${sc.border}` : 'none',
    };
    const hoveredContainerStyle: React.CSSProperties = { backgroundColor: sc.menuHoverBg };
    const currentReleaseStyle: React.CSSProperties = {
        fontSize: 14,
        fontWeight: 400,
        color: sc.text,
    };

    const onClick = () => {
        history.push(releasePath, {
            [ROUTER_REDIRECT]: true,
        });

        dispatch(
            trackReleaseRouteEvent({
                orgId,
                appId,
                releaseId,
                eventName: AnalyticEvents.Event.ClickRelease,
                context: AnalyticEvents.Context.ReleasesTimeline,
            }),
        );
    };

    const icon = rollbackOrHotfixIcon(releaseType, rollbackTargetVersionInfo?.version, releaseSourceVersion);
    const showRollbackTextAndStyle =
        releaseType === ReleaseType.Rollback && releaseTimelinePhase === ReleaseTimelinePhase.Rollback;

    const rollbackStripeA = isHovered ? sc.menuActiveBg : sc.menuHoverBg;
    const rollbackStripeB = isHovered ? sc.menuHoverBg : sc.bg;
    const rollbackStyle =
        showRollbackTextAndStyle ?
            {
                background: `repeating-linear-gradient(
                -45deg,
                ${rollbackStripeA} 0px,
                ${rollbackStripeA} 3px,
                ${rollbackStripeB} 3px,
                ${rollbackStripeB} 6px)`,
            }
        :   {};

    return (
        <div onClick={onClick}>
            <div
                style={
                    isHovered ?
                        { ...normalContainerStyle, ...hoveredContainerStyle, ...rollbackStyle }
                    :   { ...normalContainerStyle, ...rollbackStyle }
                }
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {node}
                <Space align="center">
                    <Text style={releaseIsCurrentOrRollback ? currentReleaseStyle : normalReleaseStyle}>
                        {releaseDisplayableVersion}
                    </Text>
                    {needsTag && (
                        <Tooltip title="This release needs to be tagged.">
                            <WarningFilled style={{ color: runwayColors.statusYellow }} />
                        </Tooltip>
                    )}
                    {icon}
                </Space>
                {showPhasedText && (
                    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Text style={{ ...releaseMetaTextStyle, color: versionTextColor }}>
                            Phased {displayStringForRolloutPercentage(phasedRelease?.phasedReleasePercent)}
                        </Text>
                    </div>
                )}
                {showReleaseDate && (
                    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Text style={{ ...releaseMetaTextStyle, color: sc.releaseDateText }}>
                            {dayjs(releaseReleasedAt).format('MMM D')}
                        </Text>
                    </div>
                )}
                {!showReleaseDate && showSkippedDate && (
                    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Text style={{ ...releaseMetaTextStyle, color: sc.skippedReleaseDateText }}>
                            {dayjs(releaseSkippedAt).format('MMM D')}
                        </Text>
                    </div>
                )}
                {showRollbackTextAndStyle && (
                    <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Text style={{ ...releaseMetaTextStyle, color: versionTextColor }}>Rollback</Text>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ReleasesSidebarNode;

const releaseLinkPath: (
    releaseId: string,
    orgIdParam: string,
    appIdParam: string,
    releaseIdParam: string,
    status: ReleaseStatus,
    releaseType: ReleaseType,
    lastSelectedReleaseStepForRelease: StringKeyedDictionary<string>,
) => string = (
    releaseId,
    orgIdParam,
    appIdParam,
    releaseIdParam,
    status,
    releaseType,
    lastSelectedReleaseStepForRelease,
) => {
    let path;

    const defaultNav =
        isReleaseComplete(status) ? ReleaseNav.Rollouts
        : releaseType === ReleaseType.Rollback ? ReleaseStepType.RollbackBuild
        : ReleaseNav.Overview;

    const goToReleaseStep =
        lastSelectedReleaseStepForRelease ? (lastSelectedReleaseStepForRelease[releaseId] ?? defaultNav) : defaultNav;

    if (goToReleaseStep === ReleaseNav.Overview) {
        path = createReleaseOverviewRoute(orgIdParam, appIdParam, releaseIdParam);
    } else if (goToReleaseStep === ReleaseNav.Rollouts) {
        path = createReleaseRolloutRoute(orgIdParam, appIdParam, releaseIdParam);
    } else if (goToReleaseStep === ReleaseNav.FeatureFlags) {
        path = createFeatureFlagsRoute(orgIdParam, appIdParam, releaseIdParam);
    } else if (goToReleaseStep === ReleaseNav.ReleaseCalendar) {
        path = createReleaseCalendarRoute(orgIdParam, appIdParam, releaseIdParam);
    } else {
        path = createReleaseStepTabRoute(
            orgIdParam,
            appIdParam,
            releaseIdParam,
            goToReleaseStep,
            ReleaseStepTabType.Summary,
        );
    }

    return path;
};

const rollbackOrHotfixIcon: (
    releaseType: ReleaseType,
    targetVersion?: string,
    sourceVersion?: string,
) => React.ReactNode = (releaseType, targetVersion, sourceVersion) => {
    switch (releaseType) {
        case ReleaseType.Hotfix:
            return (
                <Tooltip title="This release was a hotfix">
                    <FireOutlined style={{ color: runwayColors.statusYellow }} />
                </Tooltip>
            );
        case ReleaseType.Rollback:
            return (
                <Tooltip
                    title={`Rollback of ${targetVersion ? `${targetVersion}` : `the previous release`}${
                        sourceVersion ? ` to ${sourceVersion}.` : ''
                    }
                    `}
                >
                    <UndoOutlined style={{ color: antGrey.grey7 }} />
                </Tooltip>
            );
        default:
            return null;
    }
};
